import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { CronStateController } from '../services/cron-state.js';
import type { CronJob, CreateCronJobRequest, UpdateCronJobRequest } from '../types/cron.js';
import '../components/shared/cp-page-subnav.js';

const SYSTEM_TABS = [
  { label: 'Settings', path: '/settings' },
  { label: 'Agenten', path: '/agents' },
  { label: 'Team & Skills', path: '/team' },
  { label: 'Agent-Skills', path: '/skills' },
  { label: 'Routinen', path: '/cron' },
  { label: 'Monitoring', path: '/monitoring' },
];

@customElement('cp-page-cron')
export class CpPageCron extends LitElement {
  createRenderRoot() { return this; }

  private _cronState = new CronStateController(this);

  @state() private _confirmDialogOpen = false;
  @state() private _confirmDialogMessage = '';
  @state() private _deleteTargetJobId = 0;
  @state() private _formOpen = false;
  @state() private _editJob: CronJob | null = null;
  @state() private _showExecutionLog = false;
  @state() private _executionLogJob: CronJob | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this._cronState.loadJobs();
  }

  // ─── Event Handlers ──────────────────────────────────────────────

  private async _handleToggle(e: CustomEvent<{ jobId: number }>): Promise<void> {
    await this._cronState.toggleJob(e.detail.jobId);
  }

  private async _handleTrigger(e: CustomEvent<{ jobId: number }>): Promise<void> {
    await this._cronState.triggerJob(e.detail.jobId);
  }

  private _handleDeleteRequest(e: CustomEvent<{ jobId: number; jobName: string }>): void {
    this._deleteTargetJobId = e.detail.jobId;
    this._confirmDialogMessage = `Routine "${e.detail.jobName}" wirklich loeschen?`;
    this._confirmDialogOpen = true;
  }

  private async _handleDeleteConfirm(): Promise<void> {
    this._confirmDialogOpen = false;
    if (!this._deleteTargetJobId) return;
    await this._cronState.deleteJob(this._deleteTargetJobId);
    this._deleteTargetJobId = 0;
    // Close execution log if showing deleted job
    if (this._executionLogJob?.id === this._deleteTargetJobId) {
      this._showExecutionLog = false;
      this._executionLogJob = null;
    }
  }

  private _handleDeleteCancel(): void {
    this._confirmDialogOpen = false;
    this._deleteTargetJobId = 0;
  }

  // ─── Form Handlers ────────────────────────────────────────────────

  private _handleCreateClick(): void {
    this._editJob = null;
    this._formOpen = true;
  }

  private _handleEditClick(e: CustomEvent<{ job: CronJob }>): void {
    this._editJob = e.detail.job;
    this._formOpen = true;
  }

  private _handleFormClosed(): void {
    this._formOpen = false;
    this._editJob = null;
  }

  private async _handleJobSave(e: CustomEvent<{ mode: string; jobId?: number; data: CreateCronJobRequest | UpdateCronJobRequest }>): Promise<void> {
    const { mode, jobId, data } = e.detail;
    if (mode === 'create') {
      await this._cronState.createJob(data as CreateCronJobRequest);
    } else if (mode === 'edit' && jobId) {
      await this._cronState.updateJob(jobId, data as UpdateCronJobRequest);
    }
  }

  // ─── Execution Log Handlers ───────────────────────────────────────

  private _handleShowExecutions(e: CustomEvent<{ job: CronJob }>): void {
    this._executionLogJob = e.detail.job;
    this._showExecutionLog = true;
    this._cronState.selectJob(e.detail.job);
  }

  private _handleLogClosed(): void {
    this._showExecutionLog = false;
    this._executionLogJob = null;
    this._cronState.clearSelection();
  }

  // ─── Render ──────────────────────────────────────────────────────

  render() {
    return html`
      <div class="cp-page cp-page-cron">
        <cp-page-subnav .tabs=${SYSTEM_TABS} activePath="/cron"></cp-page-subnav>
        ${this._renderToolbar()}
        ${this._renderContent()}
        ${this._showExecutionLog && this._executionLogJob ? html`
          <cp-cron-execution-log
            .executions=${this._cronState.executions}
            .jobName=${this._executionLogJob.name}
            @log-closed=${this._handleLogClosed}
          ></cp-cron-execution-log>
        ` : nothing}
      </div>
      <cp-cron-job-form
        .open=${this._formOpen}
        .job=${this._editJob}
        @form-closed=${this._handleFormClosed}
        @job-save=${this._handleJobSave}
      ></cp-cron-job-form>
      <cp-confirm-dialog
        .open=${this._confirmDialogOpen}
        title="Routine loeschen"
        .message=${this._confirmDialogMessage}
        confirmLabel="Loeschen"
        variant="danger"
        @dialog-confirmed=${this._handleDeleteConfirm}
        @dialog-cancelled=${this._handleDeleteCancel}
      ></cp-confirm-dialog>
    `;
  }

  private _renderToolbar() {
    const qs = this._cronState.queueStatus;
    return html`
      <div class="cp-page-cron__header">
        <div class="cp-page-cron__header-row">
          <h1 class="cp-page__title">Routinen</h1>
          <div class="cp-page-cron__header-actions">
            ${qs ? this._renderQueueBadge(qs) : nothing}
            <button class="cp-page-cron__add-btn" @click=${this._handleCreateClick}>+ Neue Routine</button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderQueueBadge(qs: { isProcessing: boolean; queuedCount: number; runningJobName: string | null }) {
    if (!qs.isProcessing && qs.queuedCount === 0) return nothing;

    const parts: string[] = [];
    if (qs.isProcessing) parts.push(`1 laeuft${qs.runningJobName ? ` (${qs.runningJobName})` : ''}`);
    if (qs.queuedCount > 0) parts.push(`${qs.queuedCount} in Queue`);
    const label = parts.join(', ');

    return html`
      <span class="cp-page-cron__queue-badge ${qs.isProcessing ? 'cp-page-cron__queue-badge--active' : ''}">
        ${label}
      </span>
    `;
  }

  private _renderContent() {
    const loadingState = this._cronState.loadingState;

    if (loadingState === 'loading' && this._cronState.jobs.length === 0) {
      return this._renderLoading();
    }
    if (loadingState === 'error') {
      return this._renderError();
    }
    if (this._cronState.jobs.length === 0) {
      return this._renderEmpty();
    }
    return this._renderJobList(this._cronState.jobs);
  }

  private _renderLoading() {
    return html`
      <div class="cp-page-cron__loading">
        ${[1, 2, 3].map(() => html`
          <div class="cp-page-cron__skeleton">
            <div class="cp-page-cron__skeleton-line cp-page-cron__skeleton-line--wide"></div>
            <div class="cp-page-cron__skeleton-line"></div>
          </div>
        `)}
      </div>
    `;
  }

  private _renderError() {
    return html`
      <div class="cp-page-cron__empty">
        <div class="cp-page-cron__empty-icon">&#9888;</div>
        <h3 class="cp-page-cron__empty-title">Fehler beim Laden</h3>
        <p class="cp-page-cron__empty-text">${this._cronState.error}</p>
        <button class="cp-page-cron__action-btn" @click=${() => this._cronState.loadJobs()}>Erneut versuchen</button>
      </div>
    `;
  }

  private _renderEmpty() {
    return html`
      <div class="cp-page-cron__empty">
        <div class="cp-page-cron__empty-icon">&#9200;</div>
        <h3 class="cp-page-cron__empty-title">Keine Routinen konfiguriert</h3>
        <p class="cp-page-cron__empty-text">Erstelle deine erste automatisierte Routine.</p>
        <button class="cp-page-cron__action-btn" @click=${this._handleCreateClick}>Neue Routine erstellen</button>
      </div>
    `;
  }

  private _renderJobList(jobs: CronJob[]) {
    return html`
      <cp-cron-job-list
        .jobs=${jobs}
        @job-toggle=${this._handleToggle}
        @job-trigger=${this._handleTrigger}
        @job-delete=${this._handleDeleteRequest}
        @job-edit=${this._handleEditClick}
        @job-show-executions=${this._handleShowExecutions}
      ></cp-cron-job-list>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-cron': CpPageCron;
  }
}
